import React, { useState, lazy } from 'react';
import { XIcon, MapPinIcon, HomeIcon, BuildingIcon, CheckCircleIcon, ChevronLeftIcon, ChevronRightIcon, BadgeCheckIcon, LayersIcon, StarIcon } from 'lucide-react';
const ProjectsSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [activeTab, setActiveTab] = useState<'overview' | 'floorplan' | 'units'>('overview');
  const project = {
    id: 1,
    title: 'Viral Shantivan',
    category: 'Residential',
    location: 'Virar West, Maharashtra',
    status: 'Ongoing',
    rera: 'P99000078855',
    description: 'Proposed Redevelopment of Viral & Shantivan Society, Virar West. A premium residential complex offering modern 1 BHK and 2 BHK apartments with retail spaces on the ground floor.',
    images: [{
      url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/qsdDED6hcK3bpaqpZuX5sy/WhatsApp_Image_2025-03-20_at_10.25.59_PM.jpg',
      label: 'Exterior View (Night)'
    }, {
      url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6vKDemLDxmQLyjfaZxqx8k/WhatsApp_Image_2025-03-20_at_10.26.00_PM.jpg',
      label: 'Exterior View (Day)'
    }, {
      url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/iKY8MrH4rB6iDMRTiYMMsA/WhatsApp_Image_2025-03-15_at_7.40.52_PM.jpg',
      label: '3D Floor Plan'
    }, {
      url: 'https://cdn.magicpatterns.com/uploads/nRoW81Yz4fSueuXAws29hU/Screenshot_2026-03-01_102437.png',
      label: 'Typical Floor Plan'
    }],
    wings: [{
      name: 'A Wing',
      units: [{
        rno: '101',
        area: 427,
        type: '1 BHK'
      }, {
        rno: '102',
        area: 618,
        type: '2 BHK'
      }, {
        rno: '103',
        area: 430,
        type: '1 BHK'
      }, {
        rno: '104',
        area: 504,
        type: '2 BHK'
      }, {
        rno: '105',
        area: 462,
        type: '1 BHK'
      }]
    }, {
      name: 'B Wing',
      units: [{
        rno: '101',
        area: 440,
        type: '1 BHK'
      }, {
        rno: '102',
        area: 513,
        type: '2 BHK'
      }, {
        rno: '103',
        area: 434,
        type: '1 BHK'
      }, {
        rno: '104',
        area: 429,
        type: '1 BHK'
      }]
    }, {
      name: 'C Wing',
      units: [{
        rno: '101',
        area: 487,
        type: '2 BHK'
      }, {
        rno: '102',
        area: 644,
        type: '2 BHK'
      }, {
        rno: '103',
        area: 422,
        type: '1 BHK'
      }]
    }],
    amenities: ['Elegant Entrance Lobby', "App-Based Visitor's Management & Society Operations", 'Terrace – Bistro, Roof Top Lounge Sitting Area & Senior Citizen Corner', 'CCTV Surveillance & Intercom', 'Designated Parking Space for Vehicles', 'D-G Set for Lifts & Common Areas', 'Various Flat Configuration Options', 'Driver Room', 'Society Office', 'Telecom Room', 'Solar Panels to Reduce Electricity Bill of Society', 'Charging Stations for E-Vehicles', 'Gas Pipeline', 'Modular Kitchen with Vitrified Tiles']
  };
  const prevImage = () => setActiveImage(prev => prev === 0 ? project.images.length - 1 : prev - 1);
  const nextImage = () => setActiveImage(prev => prev === project.images.length - 1 ? 0 : prev + 1);
  return <section id="projects" className="py-20 bg-gray-50" data-id="element-333">
      <div className="container mx-auto px-4 lg:px-8" data-id="element-334">
        {/* Section Header */}
        <div className="text-center mb-16" data-id="element-335">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-336">
            Our Projects
          </h2>
          <div className="w-20 h-1 bg-blue-800 mx-auto mb-6" data-id="element-337"></div>
          <p className="max-w-3xl mx-auto text-gray-600 text-lg" data-id="element-338">
            Discover our portfolio of premium residential developments across
            Maharashtra.
          </p>
        </div>

        {/* Project Card */}
        <div className="max-w-4xl mx-auto" data-id="element-339">
          <div className="bg-white rounded-2xl overflow-hidden shadow-lg group" data-id="element-340">
            {/* Image */}
            <div className="relative h-80 overflow-hidden" data-id="element-341">
              <img src={project.images[0].url} alt={project.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-342" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" data-id="element-343" />
              {/* Status Badge */}
              <div className="absolute top-4 right-4 bg-blue-800 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5" data-id="element-344">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block" data-id="element-345"></span>
                {project.status}
              </div>
              {/* RERA Badge */}
              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-gray-800 text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5" data-id="element-346">
                <BadgeCheckIcon className="h-3.5 w-3.5 text-blue-800" data-id="element-347" />
                MahaRERA: {project.rera}
              </div>
              {/* Bottom overlay info */}
              <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-348">
                <h3 className="text-2xl font-bold text-white mb-1" data-id="element-349">
                  {project.title}
                </h3>
                <div className="flex items-center text-white/90 text-sm" data-id="element-350">
                  <MapPinIcon className="h-4 w-4 mr-1.5 flex-shrink-0" data-id="element-351" />
                  {project.location}
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="p-6 md:p-8" data-id="element-352">
              <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-353">
                {project.description}
              </p>

              {/* Key Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-354">
                <div className="bg-blue-50 rounded-xl p-4 text-center" data-id="element-355">
                  <LayersIcon className="h-6 w-6 text-blue-800 mx-auto mb-2" data-id="element-356" />
                  <p className="text-2xl font-bold text-gray-900" data-id="element-357">3</p>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-358">Wings</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center" data-id="element-359">
                  <HomeIcon className="h-6 w-6 text-blue-800 mx-auto mb-2" data-id="element-360" />
                  <p className="text-2xl font-bold text-gray-900" data-id="element-361">1 & 2</p>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-362">BHK Types</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center" data-id="element-363">
                  <BuildingIcon className="h-6 w-6 text-blue-800 mx-auto mb-2" data-id="element-364" />
                  <p className="text-2xl font-bold text-gray-900" data-id="element-365">G+7</p>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-366">Floors</p>
                </div>
              </div>

              {/* Highlights */}
              <div className="grid grid-cols-2 gap-2 mb-6" data-id="element-367">
                <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-368">
                  <CheckCircleIcon className="h-4 w-4 text-blue-800 flex-shrink-0" data-id="element-369" />
                  Premium 1 & 2 BHK Apartments
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-370">
                  <CheckCircleIcon className="h-4 w-4 text-blue-800 flex-shrink-0" data-id="element-371" />
                  Ground Floor Retail Spaces
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-372">
                  <CheckCircleIcon className="h-4 w-4 text-blue-800 flex-shrink-0" data-id="element-373" />
                  Rooftop Amenities
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-374">
                  <CheckCircleIcon className="h-4 w-4 text-blue-800 flex-shrink-0" data-id="element-375" />
                  Modern Architecture
                </div>
              </div>

              {/* CTA */}
              <button onClick={() => {
              setIsModalOpen(true);
              setActiveTab('overview');
              setActiveImage(0);
            }} className="w-full py-3 bg-blue-800 hover:bg-blue-900 text-white rounded-lg font-medium transition-all text-sm tracking-wide" data-id="element-376">
                View Full Project Details
              </button>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="mt-16 rounded-xl overflow-hidden shadow-sm h-96 bg-gray-200" data-id="element-377">
          <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d665.0651092383707!2d72.81108228653369!3d19.447960388683413!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7a9823fc39e99%3A0x4f12ef2d8266191d!2sViral%20Apartment!5e0!3m2!1sen!2sin!4v1742555659108!5m2!1sen!2sin" className="w-full h-full" style={{
          border: 0
        }} allowFullScreen loading="lazy" title="Viral Shantivan Location" data-id="element-378" />
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={e => {
      if (e.target === e.currentTarget) setIsModalOpen(false);
    }} data-id="element-379">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl" data-id="element-380">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-6 py-4 border-b border-gray-100" data-id="element-381">
              <div data-id="element-382">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-383">
                  {project.title}
                </h2>
                <div className="flex items-center text-gray-500 text-sm mt-0.5" data-id="element-384">
                  <MapPinIcon className="h-3.5 w-3.5 mr-1" data-id="element-385" />
                  {project.location}
                </div>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Close modal" data-id="element-386">
                <XIcon className="h-5 w-5 text-gray-600" data-id="element-387" />
              </button>
            </div>

            {/* Image Gallery */}
            <div className="relative bg-gray-900 h-72 md:h-96" data-id="element-388">
              <img src={project.images[activeImage].url} alt={project.images[activeImage].label} className="w-full h-full object-cover opacity-95" data-id="element-389" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" data-id="element-390" />
              {/* Image label */}
              <div className="absolute bottom-4 left-4 bg-black/50 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full" data-id="element-391">
                {project.images[activeImage].label}
              </div>
              {/* Nav arrows */}
              <button onClick={prevImage} className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-white/20 hover:bg-white/40 text-white rounded-full transition-all" data-id="element-392">
                <ChevronLeftIcon className="h-5 w-5" data-id="element-393" />
              </button>
              <button onClick={nextImage} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-white/20 hover:bg-white/40 text-white rounded-full transition-all" data-id="element-394">
                <ChevronRightIcon className="h-5 w-5" data-id="element-395" />
              </button>
              {/* Status badge */}
              <div className="absolute top-4 right-4 bg-blue-800 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5" data-id="element-396">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block" data-id="element-397"></span>
                {project.status}
              </div>
            </div>

            {/* Thumbnail strip */}
            <div className="flex gap-2 px-6 py-3 bg-gray-50 border-b border-gray-100 overflow-x-auto" data-id="element-398">
              {project.images.map((img, idx) => <button key={idx} onClick={() => setActiveImage(idx)} className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${activeImage === idx ? 'border-blue-800 opacity-100' : 'border-transparent opacity-60 hover:opacity-80'}`} data-id="element-399">
                  <img src={img.url} alt={img.label} className="w-full h-full object-cover" data-id="element-400" />
                </button>)}
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-100 px-6" data-id="element-401">
              <button onClick={() => setActiveTab('overview')} className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'overview' ? 'border-blue-800 text-blue-800' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-402">
                Overview
              </button>
              <button onClick={() => setActiveTab('floorplan')} className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'floorplan' ? 'border-blue-800 text-blue-800' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-403">
                Floor Plan
              </button>
              <button onClick={() => setActiveTab('units')} className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'units' ? 'border-blue-800 text-blue-800' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-404">
                Unit Configuration
              </button>
            </div>

            {/* Tab Content */}
            <div className="p-6" data-id="element-405">
              {/* Overview Tab */}
              {activeTab === 'overview' && <div data-id="element-406">
                  <div className="flex flex-wrap gap-3 mb-6" data-id="element-407">
                    <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-800 text-xs font-semibold px-3 py-1.5 rounded-full" data-id="element-408">
                      <BadgeCheckIcon className="h-3.5 w-3.5" data-id="element-409" />
                      MahaRERA: {project.rera}
                    </span>
                    <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1.5 rounded-full" data-id="element-410">
                      <HomeIcon className="h-3.5 w-3.5" data-id="element-411" />
                      {project.category}
                    </span>
                    <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1.5 rounded-full" data-id="element-412">
                      <MapPinIcon className="h-3.5 w-3.5" data-id="element-413" />
                      {project.location}
                    </span>
                  </div>

                  <p className="text-gray-600 leading-relaxed mb-8" data-id="element-414">
                    {project.description}
                  </p>

                  <h4 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-415">
                    <StarIcon className="h-4 w-4 text-blue-800" data-id="element-416" />
                    Key Amenities & Features
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-417">
                    {project.amenities.map((amenity, idx) => <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3" data-id="element-418">
                        <CheckCircleIcon className="h-4 w-4 text-blue-800 flex-shrink-0" data-id="element-419" />
                        <span className="text-sm text-gray-700" data-id="element-420">{amenity}</span>
                      </div>)}
                  </div>
                </div>}

              {/* Floor Plan Tab */}
              {activeTab === 'floorplan' && <div data-id="element-421">
                  <p className="text-gray-600 text-sm mb-4" data-id="element-422">
                    Typical floor plan for Viral Shantivan — Proposed
                    Redevelopment of Viral & Shantivan Society, Virar West.
                  </p>
                  <div className="rounded-xl overflow-hidden border border-gray-200" data-id="element-423">
                    <img src="https://cdn.magicpatterns.com/uploads/nRoW81Yz4fSueuXAws29hU/Screenshot_2026-03-01_102437.png" alt="Typical Floor Plan" className="w-full h-auto" data-id="element-424" />
                  </div>
                  <div className="mt-4 rounded-xl overflow-hidden border border-gray-200" data-id="element-425">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/iKY8MrH4rB6iDMRTiYMMsA/WhatsApp_Image_2025-03-15_at_7.40.52_PM.jpg" alt="3D Floor Plan" className="w-full h-auto" data-id="element-426" />
                  </div>
                </div>}

              {/* Units Tab */}
              {activeTab === 'units' && <div className="space-y-8" data-id="element-427">
                  {project.wings.map(wing => <div key={wing.name} data-id="element-428">
                      <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-429">
                        <LayersIcon className="h-4 w-4 text-blue-800" data-id="element-430" />
                        {wing.name}
                      </h4>
                      <div className="rounded-xl overflow-hidden border border-gray-200" data-id="element-431">
                        <table className="w-full text-sm" data-id="element-432">
                          <thead data-id="element-433">
                            <tr className="bg-blue-800 text-white" data-id="element-434">
                              <th className="text-left px-4 py-3 font-semibold" data-id="element-435">
                                R. No.
                              </th>
                              <th className="text-left px-4 py-3 font-semibold" data-id="element-436">
                                Type
                              </th>
                              <th className="text-left px-4 py-3 font-semibold" data-id="element-437">
                                Usable Area
                              </th>
                            </tr>
                          </thead>
                          <tbody data-id="element-438">
                            {wing.units.map((unit, idx) => <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-id="element-439">
                                <td className="px-4 py-3 text-gray-700 font-medium" data-id="element-440">
                                  {unit.rno}
                                </td>
                                <td className="px-4 py-3" data-id="element-441">
                                  <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${unit.type === '2 BHK' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`} data-id="element-442">
                                    {unit.type}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-gray-600" data-id="element-443">
                                  {unit.area} sq ft
                                </td>
                              </tr>)}
                          </tbody>
                        </table>
                      </div>
                    </div>)}

                  <div className="bg-blue-50 rounded-xl p-4 text-sm text-blue-800 flex items-start gap-2" data-id="element-444">
                    <BadgeCheckIcon className="h-4 w-4 flex-shrink-0 mt-0.5" data-id="element-445" />
                    <span data-id="element-446">
                      <strong data-id="element-447">MahaRERA Registration:</strong> {project.rera} —
                      All unit configurations are as per approved MahaRERA
                      plans.
                    </span>
                  </div>
                </div>}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row gap-3" data-id="element-448">
              <a href="#contact" onClick={() => setIsModalOpen(false)} className="flex-1 text-center py-3 bg-blue-800 hover:bg-blue-900 text-white rounded-lg font-medium transition-all text-sm" data-id="element-449">
                Schedule a Site Visit
              </a>
              <button onClick={() => setIsModalOpen(false)} className="flex-1 py-3 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-all text-sm" data-id="element-450">
                Close
              </button>
            </div>
          </div>
        </div>}
    </section>;
};
export default ProjectsSection;